import AccountCombobox from "@app/components/account-combobox";
import type { AccountOption } from "@app/components/account-combobox";
import { useState } from "react";

export type RuleAccountFilterValue = {
  sourceIds: string[];
  destinationIds: string[];
};

export const EMPTY_RULE_ACCOUNT_FILTER: RuleAccountFilterValue = {
  sourceIds: [],
  destinationIds: [],
};

const accountLabel = (a: AccountOption) =>
  `${a.nickname || a.name}${a.last4 ? ` ••${a.last4}` : ""}`;

// Optional account scoping for a suggested rule. Collapsed (off) by default — the
// rule stays unfiltered until the user opens this and picks accounts. "source" is
// the txn's own account; "destination" is the linked counter-account
// (other_bank_account_id), only shown for attribution/classification rules since an
// internal-link rule's destination is already its target.
export default function RuleAccountFilter({
  accounts,
  value,
  onChange,
  showDestination,
  prefillSourceIds = [],
}: {
  accounts: ReadonlyArray<AccountOption>;
  value: RuleAccountFilterValue;
  onChange: (next: RuleAccountFilterValue) => void;
  showDestination: boolean;
  prefillSourceIds?: ReadonlyArray<string>;
}) {
  const [open, setOpen] = useState(false);

  const byId = (id: string) => accounts.find((a) => a.id === id);
  const add = (key: "sourceIds" | "destinationIds", id?: string) => {
    if (!id || value[key].includes(id)) return;
    onChange({ ...value, [key]: [...value[key], id] });
  };
  const remove = (key: "sourceIds" | "destinationIds", id: string) =>
    onChange({ ...value, [key]: value[key].filter((x) => x !== id) });

  const total = value.sourceIds.length + value.destinationIds.length;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs text-gray-500 hover:text-gray-800 underline"
      >
        {total > 0
          ? `Limited to ${total} account${total === 1 ? "" : "s"} — edit`
          : "Limit to accounts (optional)"}
      </button>
    );
  }

  const chips = (key: "sourceIds" | "destinationIds") =>
    value[key].length > 0 && (
      <div className="flex flex-wrap gap-1">
        {value[key].map((id) => {
          const a = byId(id);
          return (
            <span
              key={id}
              className="inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-xs"
            >
              {a ? accountLabel(a) : id}
              <button
                type="button"
                onClick={() => remove(key, id)}
                className="text-gray-400 hover:text-gray-700"
                aria-label="Remove account"
              >
                ×
              </button>
            </span>
          );
        })}
      </div>
    );

  const unusedPrefill = prefillSourceIds.filter(
    (id) => !value.sourceIds.includes(id)
  );

  return (
    <div className="space-y-2 rounded border border-gray-200 p-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-600">Limit to accounts</span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-xs text-gray-400 hover:text-gray-700"
        >
          Done
        </button>
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <label className="text-xs text-gray-500">Source accounts</label>
          {unusedPrefill.length > 0 && (
            <button
              type="button"
              onClick={() =>
                onChange({
                  ...value,
                  sourceIds: [...value.sourceIds, ...unusedPrefill],
                })
              }
              className="text-xs text-emerald-600 hover:text-emerald-700"
            >
              Use selection's accounts
            </button>
          )}
        </div>
        {chips("sourceIds")}
        <AccountCombobox
          options={accounts}
          autoFocus={false}
          allowCreate={false}
          placeholder="Add a source account…"
          onPick={(c) => add("sourceIds", c.bankAccountId)}
        />
      </div>

      {showDestination && (
        <div className="space-y-1">
          <label className="text-xs text-gray-500">
            Destination accounts <span className="text-gray-400">(linked transfers)</span>
          </label>
          {chips("destinationIds")}
          <AccountCombobox
            options={accounts}
            autoFocus={false}
            allowCreate={false}
            placeholder="Add a destination account…"
            onPick={(c) => add("destinationIds", c.bankAccountId)}
          />
        </div>
      )}
    </div>
  );
}

// The GraphQL createRule filter args for a filter value — each list omitted when
// empty so that dimension stays unfiltered (the backend treats nil as "all").
export function ruleFilterInput(value: RuleAccountFilterValue): {
  sourceBankAccountIds?: string[];
  destinationFilterBankAccountIds?: string[];
} {
  return {
    sourceBankAccountIds: value.sourceIds.length ? value.sourceIds : undefined,
    destinationFilterBankAccountIds: value.destinationIds.length
      ? value.destinationIds
      : undefined,
  };
}
